import axios from 'axios';
import { isBlankOrNullString } from './stringUtils';
import { deleteJwtToken, getJwtToken, jwtTokenExists, setJwtToken } from './tokenUtils';

const AxiosCreate = ({ baseUrl }) => {
    if (baseUrl != null) {
        return axios.create({
            baseURL: baseUrl,
            timeout: Number.parseInt(process.env.REACT_APP_API_REQUEST_TIMEOUT),
        });
    }
    return axios.create({
        timeout: Number.parseInt(process.env.REACT_APP_API_REQUEST_TIMEOUT),
    });
};

export const AxiosInstanceBasic = ({ baseUrl }) => {
    const _instance = AxiosCreate({ baseUrl: baseUrl });
    _instance.interceptors.request.use(
        (config) => ({
            ...config,
            headers: {
                ...config.headers,
                Authorization: jwtTokenExists() ? `Bearer ${getJwtToken()}` : config.headers.Authorization,
            },
        }),
        (error) => Promise.reject(error)
    );
    return _instance;
};

const fetchToken = async ({ loginUrl }) => {
    try {
        // noinspection JSCheckFunctionSignatures
        const { data } = await AxiosInstanceBasic({ baseUrl: loginUrl }).get(
            process.env.REACT_APP_API_TOKEN_REFRESH_ENDPOINT
        );
        if (isBlankOrNullString(data?.jwtToken)) {
            deleteJwtToken();
            return false;
        }
        setJwtToken(data?.jwtToken);
    } catch (e) {
        deleteJwtToken();
        return false;
    }
    return true;
};

const refreshToken = ({ loginUrl, instance, refreshing, queue, setRefreshing, clearQueue }) => {
    if (refreshing) return;
    setRefreshing(true);
    // noinspection JSCheckFunctionSignatures
    AxiosInstanceBasic({ baseUrl: loginUrl })
        .get(process.env.REACT_APP_API_TOKEN_REFRESH_ENDPOINT)
        .then(({ data }) => {
            if (isBlankOrNullString(data?.jwtToken)) {
                deleteJwtToken();
            } else {
                setJwtToken(data.jwtToken);
                queue.forEach(({ config, resolve, reject }) => {
                    instance.request(config).then(resolve).catch(reject);
                });
            }
        })
        .catch(() => {
            deleteJwtToken();
        })
        .finally(() => {
            setRefreshing(false);
            clearQueue();
        });
};

export const AxiosInstanceWithRefresh = ({
                                             baseUrl,
                                             loginUrl,
                                             refreshing,
                                             queue,
                                             setRefreshing,
                                             pushQueue,
                                             clearQueue,
                                         }) => {
    const _instance = AxiosCreate({ baseUrl: baseUrl });
    _instance.interceptors.request.use(
        async (config) => {
            const controller = new AbortController();

            if (!jwtTokenExists()) {
                const wasSuccessful = await fetchToken({ loginUrl: loginUrl });
                if (!wasSuccessful) {
                    controller.abort();
                }
            }

            return {
                ...config,
                signal: controller.signal,
                headers: {
                    ...config.headers,
                    Authorization: jwtTokenExists() ? `Bearer ${getJwtToken()}` : config.headers.Authorization,
                },
            };
        },
        (error) => Promise.reject(error)
    );
    _instance.interceptors.response.use(
        (response) => response,
        (error) => {
            const config = error.config;
            if (error.response?.status === 401) {
                config.headers['X-Retry-Count'] = `${Number.parseInt(config?.headers['X-Retry-Count'] ?? '0') + 1}`;
                if (
                    Number.parseInt(config.headers['X-Retry-Count']) <= Number.parseInt(process.env.REACT_APP_API_REQUEST_RETRIES)
                ) {
                    refreshToken({
                        loginUrl: loginUrl,
                        instance: _instance,
                        refreshing: refreshing,
                        queue: queue,
                        setRefreshing: setRefreshing,
                        clearQueue: clearQueue,
                    });
                    return new Promise((resolve, reject) => pushQueue({ config, resolve, reject }));
                }
            }
            return Promise.reject(error);
        }
    );
    return _instance;
};
